import { Inject, Injectable } from '@angular/core';
import { arrayIntersect } from '@wz-utilities/array.utilities';
import { getEnumValues } from '@wz-utilities/enum.utilities';
import { objectToArray } from '@wz-utilities/object.utilities';
import * as _ from 'lodash';
import { FormsModels } from '../../../../../models/forms.model';
import { RESOURCE_TOKEN } from '../../wz-core/wz-core-injection-tokens';
import { ILogger } from '../logger/ilogger';
import { LoggerService } from '../logger/logger.service';
import { enumResources } from './enum-resources';
import { errorResources } from './error-resources';
import { sharedResources } from './shared-resources';

const generalFallbackResource = 'Une erreur est survenue.';

@Injectable({
  providedIn: 'root',
})
export class ResourcesService {
  private logger: ILogger;

  private resources!: { [key: string]: string };

  private resourcesAsArray: any[] = [];

  constructor(
    private loggerService: LoggerService,
    @Inject(RESOURCE_TOKEN) appResources: { [key: string]: any },
  ) {
    this.logger = this.loggerService.getLogger('Resources');
    this.loadResources(appResources);
    this.resourcesAsArray = objectToArray(enumResources);
  }

  public getResource(key: string, ...args: any[]): string {
    return this.resources[key] ? this.resources[key] : key;
  }
  public getResourceEnum(value: number | string, enumKey: string): string;
  // tslint:disable-next-line:unified-signatures
  public getResourceEnum<T>(value: number, type: T): string;
  public getResourceEnum(value: number, param: string | any) {
    if (typeof param === 'string') {
      return this.getResourceByEnumKey(value, param);
    }

    return this.getResourceByEnumType(value, param);
  }

  public getResourceAsObject(
    enumKey: keyof typeof enumResources,
    excludedValues?: number[],
    addEmpty: boolean = false,
  ) {
    const enumType: any = enumResources[enumKey];
    if (enumType.$ENUM_MAPPING) {
      let dictionary = _.fromPairs(
        getEnumValues(enumType.$ENUM_MAPPING)
          .filter((v) => !excludedValues || excludedValues.indexOf(+v) === -1)
          .map((enumValue: any) => [enumType[enumValue], enumValue]),
      ) as any;

      if (addEmpty) dictionary['Vide'] = null;
      return dictionary;
    }
  }

  public getResourceEnumList(
    enumKey: keyof typeof enumResources,
    includedValues?: number[],
    excludedValues?: number[],
  ): FormsModels.SelectListItem[] {
    const enumType: any = enumResources[enumKey];
    if (enumType.$ENUM_MAPPING) {
      let list = getEnumValues(enumType.$ENUM_MAPPING);
      if (includedValues) {
        list = list.filter((v) => includedValues.indexOf(+v) !== -1);
      } else if (excludedValues) {
        list = list.filter((v) => excludedValues.indexOf(+v) === -1);
      }

      // Later, remove YES_NO from enum
      const valuesAsBool = enumKey === 'YESNO_ENUM';

      return list.map((enumValue: any) => {
        let select: FormsModels.SelectListItem = {
          value: valuesAsBool ? !!enumValue : enumValue,
          text: enumType[enumValue],
        };

        return select;
      });
    }

    throw Error(`${enumKey} had no ENUM_MAPPING associated`);
  }

  public getErrorResource(errorCode: number, fallbackRes?: string) {
    let resource = errorResources[errorCode];
    if (!resource) {
      this.logger.warn(`Error resource '${errorCode}' not found.`);
      resource = fallbackRes || generalFallbackResource;
    }

    return resource;
  }

  public getAllErrorResources(): { code: string; label: string }[] {
    return Object.keys(errorResources).map((p: any) => ({
      code: p,
      label: errorResources[p],
    }));
  }

  private getResourceByEnumType(enumVal: number, type: any): string {
    const enumRes = this.resourcesAsArray.find((r) => r.$ENUM_MAPPING === type);
    if (enumRes && enumRes[enumVal]) {
      return enumRes[enumVal];
    }

    return '';
  }

  public getResourceByEnumKey(enumValue: number, enumKey: string) {
    const enumKeyBuilt = `ENUM_${enumKey}_${enumValue}`;
    const enumText = this.getResource(enumKeyBuilt);

    if (!enumText || enumText === enumKeyBuilt) {
      const defaultKeyBuilt = `ENUM_${enumKey}_DEFAULT`;
      const defaultText = this.getResource(defaultKeyBuilt);
      if (defaultText && defaultText !== defaultKeyBuilt) {
        return defaultText;
      }
    }

    return enumText;
  }

  private loadResources(appResources: { [key: string]: any }) {
    this.logger.groupStart('Loading resources');
    const flatAppResources = this.flatifyResources(appResources);
    const flatSharedResources = this.flatifyResources(sharedResources);

    this.logger.log('Merging Shared and App ressources');
    this.resources = this.mergeResources(flatSharedResources, flatAppResources);
    this.logger.groupEnd();
  }

  private mergeResources(
    source: { [key: string]: string },
    override: { [key: string]: string },
  ): { [key: string]: string } {
    const collisionKeys = arrayIntersect(
      Object.keys(source),
      Object.keys(override),
    );
    if (collisionKeys.length > 0) {
      this.logger.warn(
        `${collisionKeys.length} collision were found`,
        collisionKeys,
      );
      this.logger.warn(
        `The following values will be used`,
        collisionKeys.map((key) => {
          return { key, usedValue: override[key], overridenValue: source[key] };
        }),
      );
    }

    return Object.assign({}, source, override);
  }

  /**
   * Flatify an object with multiple levels to get a flat object dictionary (key:string)
   * @param appResources A javascript object (with multiple levels)
   */
  private flatifyResources(appResources: { [key: string]: any }): {
    [key: string]: string;
  } {
    let toReturn: { [key: string]: string } = {};

    for (let key in appResources) {
      if (appResources.hasOwnProperty(key)) {
        if (typeof appResources[key] === 'string') {
          toReturn[key] = appResources[key];
          continue;
        }

        let flatObject: { [key: string]: string } = this.flatifyResources(
          appResources[key],
        );
        for (let subKey in flatObject) {
          if (flatObject.hasOwnProperty(subKey)) {
            const builtKey = subKey.length === 0 ? key : key + '_' + subKey;
            toReturn[builtKey] = flatObject[subKey];
          }
        }
      }
    }
    return toReturn;
  }
}
